import { Injectable, signal, computed, effect } from '@angular/core';
import { CookiesService } from './cookies/cookies.service';

export type ServiceMode = 'ALS' | 'DoorToDoor';

export interface ServiceModeConfig {
  mode: ServiceMode;
  backgroundColor: string;
  textColor: string;
  iconColor: string;
  primaryColor: string;
  hoverColor: string;
}

@Injectable({
  providedIn: 'root',
})
export class ServiceModeService {
  // Available service modes configuration
  private readonly modeConfigs: Record<ServiceMode, ServiceModeConfig> = {
    // ALS: {
    //   mode: 'ALS',
    //   backgroundColor: '#0B133D', // Blue
    //   textColor: '#ffffff',
    //   iconColor: '#ffffff',
    // },
    // DoorToDoor: {
    //   mode: 'DoorToDoor',
    //   backgroundColor: '#ffffff', // White
    //   textColor: '#000000',
    //   iconColor: '#000000',
    // },

    ALS: {
      mode: 'ALS',
      backgroundColor: '#0B133D', 
      textColor: '#ffffff',
      iconColor: '#ffffff',
      primaryColor: '#ff5722', 
      hoverColor: '#ff8f00',
    },
    DoorToDoor: {
      mode: 'DoorToDoor',
      backgroundColor: '#ffffff', 
      textColor: '#000000',
      iconColor: '#000000',
      primaryColor: '#304CDC', 
      hoverColor: '#1f3bb5', // Default hover color for D2D mode
    },
  };

  // Current service mode signal
  private currentModeSignal = signal<ServiceMode>('ALS');

  // Public readonly computed values
  readonly currentMode = this.currentModeSignal.asReadonly();
  readonly currentModeConfig = computed(
    () => this.modeConfigs[this.currentModeSignal()],
  );

  // Cookie keys for persistence
  private readonly PER_USER_MODE_KEY = 'user_service_mode';
  private readonly GLOBAL_MODE_KEY = 'global_service_mode';

  constructor(private readonly cookieService: CookiesService) {
    // Initialize mode from cookies on service creation
    this.initializeModeFromStorage();

    // Auto-save mode changes to cookies
    effect(() => {
      const mode = this.currentModeSignal();
      this.saveMode(mode);
    });
  }

  /**
   * Initialize service mode from storage
   */
  // private initializeModeFromStorage(): void {
  //   // Try per-user mode first, then global mode, then default to DoorToDoor
  //   const perUserMode = this.cookieService.get(
  //     this.PER_USER_MODE_KEY,
  //   ) as ServiceMode;
  //   const globalMode = this.cookieService.get(
  //     this.GLOBAL_MODE_KEY,
  //   ) as ServiceMode;

  //   const savedMode = perUserMode || globalMode || 'DoorToDoor';

  //   if (this.isValidMode(savedMode)) {
  //     this.currentModeSignal.set(savedMode);
  //   }
  // }

  private initializeModeFromStorage(): void {
    const perUserModeRaw = this.cookieService.get(this.PER_USER_MODE_KEY);
    const globalModeRaw = this.cookieService.get(this.GLOBAL_MODE_KEY);

    const savedMode =
      this.normalizeMode(perUserModeRaw) ??
      this.normalizeMode(globalModeRaw) ??
      'ALS';

    this.currentModeSignal.set(savedMode);
  }

  private normalizeMode(value: string | null | undefined): ServiceMode | null {
    if (value === 'ALS') return 'ALS';
    if (value === 'DoorToDoor') return 'DoorToDoor';
    return null;
  }

  /**
   * Switch service mode (for SuperAdmin and Admin)
   */
  switchMode(mode: ServiceMode, userId?: string): void {
    if (this.isValidMode(mode)) {
      this.currentModeSignal.set(mode);
    }
  }

  /**
   * Save mode to cookies
   * IMPLEMENTATION NOTE: Choose one strategy based on your requirement
   */
  private saveMode(mode: ServiceMode): void {
    // OPTION 1: Per-user storage (recommended for your use case)
    this.savePerUserMode(mode);

    // OPTION 2: Global storage (uncomment if you want global mode)
    // this.saveGlobalMode(mode);
  }

  /**
   * Save mode per user (affects only the logged-in SuperAdmin)
   */
  private savePerUserMode(mode: ServiceMode): void {
    // Store with user identifier to make it user-specific
    // Cookie expires in 365 days
    this.cookieService.set(this.PER_USER_MODE_KEY, mode, 365);
  }

  /**
   * Save mode globally (affects all users in the system)
   */
  private saveGlobalMode(mode: ServiceMode): void {
    // Store globally - all users will see this mode
    // Cookie expires in 365 days
    this.cookieService.set(this.GLOBAL_MODE_KEY, mode, 365);
  }

  /**
   * Clear mode from storage
   */
  clearMode(): void {
    this.cookieService.remove(this.PER_USER_MODE_KEY);
    this.cookieService.remove(this.GLOBAL_MODE_KEY);
    this.currentModeSignal.set('ALS'); // Reset to default
  }

  /**
   * Get available modes for dropdown
   */
  getAvailableModes(): ServiceMode[] {
    return ['ALS', 'DoorToDoor'];
  }

  /**
   * Get mode display name
   */
  getModeDisplayName(mode: ServiceMode): string {
    return mode === 'DoorToDoor' ? 'Door to Door' : 'ALS';
  }

  /**
   * Check if a mode is valid
   */
  private isValidMode(mode: string): mode is ServiceMode {
    return mode === 'ALS' || mode === 'DoorToDoor';
  }

  /**
   * Check if current mode matches
   */
  isMode(mode: ServiceMode): boolean {
    return this.currentModeSignal() === mode;
  }
}
